import { HttpClient, HttpEvent, HttpEventType, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AppConfig } from '@iupics-config/app.config.service';
import { filter, map, Observable } from 'rxjs';
import {
  DocAccess,
  DocBulkResult,
  DocTagRename,
  DocDetail,
  DocDuplicate,
  DocFolders,
  DocItem,
  DocLink,
  DocPage,
  DocQueryBody,
  DocRecord,
  DocTable,
  DocTag,
  DocTagValue,
  DocSavedView,
} from './documents.models';

/** HTTP access to the Documents window backend (DocumentsWS, config path "documents"): files only through ws */
@Injectable({ providedIn: 'root' })
export class DocumentsService {
  readonly #http = inject(HttpClient);
  readonly #config = inject(AppConfig);

  get url(): string {
    return this.#config.getBackendResource('documents');
  }

  /** ws root (short-lived links are relative to it) */
  get wsRoot(): string {
    return this.url.replace(/\/documents\/?$/, '');
  }

  folders(): Observable<DocFolders> {
    return this.#http.get<DocFolders>(`${this.url}/folders`);
  }

  search(body: DocQueryBody): Observable<DocPage> {
    return this.#http.post<DocPage>(`${this.url}/search`, body);
  }

  detail(docId: string, q: string | null): Observable<DocDetail> {
    const fromObject: Record<string, string> = { docId };
    if (q) fromObject['q'] = q;
    return this.#http.get<DocDetail>(`${this.url}/item`, { params: new HttpParams({ fromObject }) });
  }

  /** URL of the file through ws (authenticated: fetched with the bearer token, e.g. by iu-preview-doc) */
  contentUrl(docId: string, inline: boolean): string {
    return `${this.url}/content?docId=${encodeURIComponent(docId)}&inline=${inline}`;
  }

  /** thumb: read to draw a list thumbnail (logged as such by the server, not as a view) */
  content(docId: string, thumb = false): Observable<Blob> {
    return this.#http.get(this.contentUrl(docId, false) + (thumb ? '&thumb=true' : ''), { responseType: 'blob' });
  }

  /** rename (or merge) a tag on every document the role may change; apply false: counts only */
  renameTag(from: string, to: string, apply: boolean): Observable<DocTagRename> {
    return this.#http.post<DocTagRename>(`${this.url}/tags/rename`, { from, to, apply });
  }

  /** who viewed or downloaded the document, most recent first */
  access(docId: string): Observable<DocAccess[]> {
    return this.#http.get<DocAccess[]>(`${this.url}/access`, { params: new HttpParams({ fromObject: { docId } }) });
  }

  /** the file with download progress: progress events, then one event with the blob */
  contentWithProgress(docId: string): Observable<{ loaded: number; total: number | null; blob: Blob | null }> {
    return this.#http
      .get(this.contentUrl(docId, true), { responseType: 'blob', reportProgress: true, observe: 'events' })
      .pipe(
        filter((e) => e.type === HttpEventType.DownloadProgress || e.type === HttpEventType.Response),
        map((e) =>
          e.type === HttpEventType.Response
            ? { loaded: e.body?.size ?? 0, total: e.body?.size ?? null, blob: e.body }
            : { loaded: (e as { loaded: number }).loaded, total: (e as { total?: number }).total ?? null, blob: null }
        )
      );
  }

  link(docId: string, inline: boolean): Observable<DocLink> {
    return this.#http.post<DocLink>(`${this.url}/link`, { docId, inline });
  }

  /** short-lived link to a zip of the documents (the browser downloads it) */
  zipLink(docIds: string[]): Observable<DocLink> {
    return this.#http.post<DocLink>(`${this.url}/zip-link`, { docIds });
  }

  upload(
    file: File,
    tableId: number | null,
    recordId: number | null,
    allRoles: boolean,
    tags: Record<string, string>,
    uploadKey: string
  ): Observable<HttpEvent<DocItem>> {
    const form = new FormData();
    form.append('file', file, file.name);
    if (tableId) form.append('tableId', String(tableId));
    if (recordId) form.append('recordId', String(recordId));
    form.append('allRoles', String(allRoles));
    form.append('uploadKey', uploadKey);
    const filled = Object.fromEntries(Object.entries(tags).filter(([, v]) => v?.trim()));
    if (Object.keys(filled).length) form.append('tags', JSON.stringify(filled));
    return this.#http.post<DocItem>(`${this.url}/upload`, form, { reportProgress: true, observe: 'events' });
  }

  /** removes what a cancelled upload stored */
  cancelUpload(uploadKey: string): Observable<{ removed: number }> {
    return this.#http.post<{ removed: number }>(`${this.url}/upload-cancel`, { uploadKey });
  }

  /** tags a search can use (user tags and record link tags) */
  tagKeys(): Observable<DocTag[]> {
    return this.#http.get<DocTag[]>(`${this.url}/tag-keys`);
  }

  /** frequent values of a tag (containing q) with their counts */
  tagValues(key: string, q: string): Observable<DocTagValue[]> {
    return this.#http.get<DocTagValue[]>(`${this.url}/tag-values`, { params: new HttpParams({ fromObject: { key, q } }) });
  }

  tables(q: string): Observable<DocTable[]> {
    return this.#http.get<DocTable[]>(`${this.url}/tables`, { params: new HttpParams({ fromObject: { q } }) });
  }

  records(tableId: number, q: string): Observable<DocRecord[]> {
    return this.#http.get<DocRecord[]>(`${this.url}/records`, { params: new HttpParams({ fromObject: { tableId: String(tableId), q } }) });
  }

  /** tableId null: unlink */
  linkRecord(docIds: string[], tableId: number | null, recordId: number | null): Observable<DocBulkResult> {
    return this.#http.post<DocBulkResult>(`${this.url}/link-record`, { docIds, tableId, recordId });
  }

  bulkTags(docIds: string[], key: string, value: string, mode: 'set' | 'add' | 'clear'): Observable<DocBulkResult> {
    return this.#http.post<DocBulkResult>(`${this.url}/bulk-tags`, { docIds, key, value, mode });
  }

  views(): Observable<DocSavedView[]> {
    return this.#http.get<DocSavedView[]>(`${this.url}/views`);
  }

  saveView(name: string, query: string): Observable<DocSavedView> {
    return this.#http.post<DocSavedView>(`${this.url}/views`, { name, query });
  }

  deleteView(id: string): Observable<void> {
    return this.#http.delete<void>(`${this.url}/views`, { params: new HttpParams({ fromObject: { id } }) });
  }

  duplicates(hashes: string[]): Observable<DocDuplicate[]> {
    return this.#http.post<DocDuplicate[]>(`${this.url}/duplicates`, { hashes });
  }

  retryExtraction(docId: string): Observable<void> {
    return this.#http.post<void>(`${this.url}/ocr`, { docId });
  }

  uploadVersion(docId: string, file: File): Observable<HttpEvent<DocItem>> {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('docId', docId);
    return this.#http.post<DocItem>(`${this.url}/version`, form, { reportProgress: true, observe: 'events' });
  }

  restoreVersion(docId: string, number: number): Observable<DocItem> {
    return this.#http.post<DocItem>(`${this.url}/version/restore`, { docId, number });
  }

  versionContent(docId: string, number: number): Observable<Blob> {
    return this.#http.get(`${this.url}/version/content`, {
      params: new HttpParams({ fromObject: { docId, number: String(number) } }),
      responseType: 'blob',
    });
  }

  update(docId: string, change: { fileName?: string; tags?: Record<string, string> }): Observable<DocItem> {
    return this.#http.patch<DocItem>(`${this.url}/item`, change, { params: new HttpParams({ fromObject: { docId } }) });
  }

  trash(docIds: string[]): Observable<DocBulkResult> {
    return this.#http.post<DocBulkResult>(`${this.url}/trash`, { docIds });
  }

  restore(docIds: string[]): Observable<DocBulkResult> {
    return this.#http.post<DocBulkResult>(`${this.url}/restore`, { docIds });
  }

  purge(docIds: string[]): Observable<DocBulkResult> {
    return this.#http.post<DocBulkResult>(`${this.url}/purge`, { docIds });
  }

  favourite(docId: string, favourite: boolean): Observable<void> {
    return this.#http.post<void>(`${this.url}/favourite`, { docId, favourite });
  }

  tags(): Observable<DocTag[]> {
    return this.#http.get<DocTag[]>(`${this.url}/tags`);
  }
}
